import {
  formatOptimizationDatasetNameForApi,
  normalizeOptimizationDatasetNames,
} from '../../utils/optimizationDatasetName';
import { mergeConfiguratorModelOptions } from '../../utils/configuratorModels';
import type { StateCreator } from 'zustand';
import type { OptimizationState } from '../optimizationStore';
import { ChatMessage } from '../storeShared';

/** Single-agent optimization, its graphs and chat, and the settings shared by every run (dataset, model, history). (AIT-423) */
export interface SingleAgentSlice {
  // Основные данные
  initialPrompt: string;
  botPrompt: string;
  selectedPrompt: string;
  selectedPromptIndex: string;
  optimizedPrompts: string[];
  iterationResults: Array<{
    prompt: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }>;
  isOptimized: boolean;

  // Состояние оптимизации
  isOptimizing: boolean;
  optimizationProgress: number;

  // Экспоненциальный прогресс-бар
  progressStartTime: number | null; // Время начала текущей стадии (timestamp)
  currentProgressStage: number; // Текущая стадия (0, 1, 2, ..., iterations-1)
  expectedIterationTime: number; // Ожидаемое время одной итерации в секундах

  // Настройки
  selectedDataset: string;
  /** Selected dataset labels for optimization (API: dataset_name string | string[]). */
  selectedDatasets: string[];
  selectedModel: string;
  temperature: number;
  iterations: number;
  optimizationType: 'standard' | 'with_integration'; // Новое поле для типа оптимизации

  // Флаг инициализации для отслеживания первой загрузки дефолтов
  isDefaultsLoaded: boolean;

  optimizedTemperature?: number | null;

  // Доступные опции
  availableDatasets: string[];
  availableModels: string[];

  // Данные для графиков
  embeddingData: number[];
  iterationData: number[];
  // Качество и токены актора — отдельные абсолютные ряды, не индексируются от iterationData
  currentIteration: number;
  graphSliderValue: number;

  // Чат для Single-Agent
  singleAgentChatMessages: {
    before: ChatMessage[];
    after: ChatMessage[];
  };
  singleAgentChatInputs: {
    before: string;
    after: string;
  };

  // Действия
  setInitialPrompt: (prompt: string) => void;
  setBotPrompt: (prompt: string) => void;
  setSelectedPrompt: (prompt: string) => void;
  setSelectedPromptIndex: (index: string) => void;
  setOptimizedPrompts: (prompts: string[]) => void;
  setIterationResults: (
    results: Array<{
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      iteration: number;
      seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
      temperature?: number;
    }>
  ) => void;
  setIsOptimized: (isOptimized: boolean) => void;
  setIsOptimizing: (isOptimizing: boolean) => void;
  setOptimizationProgress: (progress: number) => void;
  setProgressStartTime: (time: number | null) => void;
  setCurrentProgressStage: (stage: number) => void;
  setExpectedIterationTime: (time: number) => void;
  setSelectedDataset: (dataset: string) => void;
  setSelectedDatasets: (datasets: string[]) => void;
  setSelectedModel: (model: string) => void;
  setTemperature: (temperature: number) => void;
  setIterations: (iterations: number) => void;
  setOptimizationType: (type: 'standard' | 'with_integration') => void; // Новое действие
  setAvailableDatasets: (datasets: string[]) => void;
  setAvailableModels: (models: string[]) => void;
  setEmbeddingData: (data: number[]) => void;
  setIterationData: (data: number[]) => void;
  setCurrentIteration: (iteration: number) => void;
  setGraphSliderValue: (value: number) => void;
  addEmbeddingPoint: (value: number) => void;
  addIterationPoint: (value: number) => void;

  // Новые функции для работы с данными из бэкенда
  addBackendEmbeddingPoint: (point: { x: number; y: number }) => void;
  addBackendEvaluationPoint: (point: { x: number; y: number }) => void;
  clearGraphData: () => void;

  // Функция для обработки результатов оптимизации из бэкенда
  processOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
  }) => void;

  // Функция для постепенного добавления промптов
  addOptimizedPrompt: (prompt: string) => void;

  // Функция для обработки промежуточных результатов
  processIntermediateResult: (result: {
    prompt?: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration?: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }) => void;

  // Сброс состояния
  resetOptimization: () => void;

  // Новые поля
  selectedIterationIndex: number;
  setSelectedIterationIndex: (index: number) => void;

  iterationsMax: number;

  // Функция для сохранения результатов в историю
  saveToHistory: () => Promise<void>;

  // Функция для загрузки конфигурации из истории
  loadFromHistory: (historyItem: {
    initial_prompt: string;
    optimized_prompts: string[];
    dataset_name: string | string[];
    model_name: string;
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name: string;
      temperature: number;
    }>;
  }) => void;

  // Действия для работы с чатами Single-Agent
  setSingleAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) => void;
  setSingleAgentChatInputs: (inputs: { before: string; after: string }) => void;
  setSingleAgentChatInput: (type: 'before' | 'after', value: string) => void;
  addSingleAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) => void;
  clearSingleAgentChat: () => void;
}

export const createSingleAgentSlice: StateCreator<
  OptimizationState,
  [['zustand/persist', unknown]],
  [],
  SingleAgentSlice
> = (set, get) => ({
  // Начальные значения
  initialPrompt: '',
  botPrompt: '',
  selectedPrompt: '',
  selectedPromptIndex: '',
  optimizedPrompts: [],
  iterationResults: [],
  isOptimized: false,
  isOptimizing: false,
  optimizationProgress: 0,
  progressStartTime: null,
  currentProgressStage: 0,
  expectedIterationTime: 60, // По умолчанию 60 секунд на итерацию
  selectedDataset: '',
  selectedDatasets: [],
  selectedModel: 'gpt-4o',
  temperature: 0.7,
  iterations: 6,
  optimizationType: 'standard', // Устанавливаем стандартный тип по умолчанию
  isDefaultsLoaded: false, // Флаг для отслеживания первой загрузки дефолтов
  availableDatasets: [],
  availableModels: mergeConfiguratorModelOptions(['gpt-4.1-mini']),
  embeddingData: [],
  iterationData: [],
  currentIteration: 0,
  graphSliderValue: 0,
  selectedIterationIndex: 0,
  iterationsMax: 10,
  optimizedTemperature: null,

  // Начальные значения для чатов Single-Agent
  singleAgentChatMessages: {
    before: [],
    after: [],
  },
  singleAgentChatInputs: {
    before: '',
    after: '',
  },

  // Действия
  setInitialPrompt: (prompt: string) => set({ initialPrompt: prompt }),
  setBotPrompt: (prompt: string) => set({ botPrompt: prompt }),
  setSelectedPrompt: (prompt: string) => set({ selectedPrompt: prompt }),
  setSelectedPromptIndex: (index: string) => set({ selectedPromptIndex: index }),
  setOptimizedPrompts: (prompts: string[]) => set({ optimizedPrompts: prompts }),
  setIterationResults: (results) => set({ iterationResults: results }),
  setIsOptimized: (isOptimized: boolean) => {
    console.log('🔧 setIsOptimized called with:', isOptimized);
    set({ isOptimized });
  },
  setIsOptimizing: (isOptimizing: boolean) => set({ isOptimizing }),
  setOptimizationProgress: (progress: number) =>
    set((state) => {
      const incoming = Math.max(0, Math.min(100, progress));
      const current = typeof state.optimizationProgress === 'number' ? state.optimizationProgress : 0;

      // ✅ Разрешаем обнуление прогресса (для повторного запуска оптимизации)
      // Если incoming === 0, значит это начало новой оптимизации - принудительно обнуляем
      const next = incoming === 0 ? 0 : Math.max(current, incoming);

      console.log('📊 setOptimizationProgress:', { current, incoming, next });

      if (next !== current) {
        return { optimizationProgress: next };
      }
      return {};
    }),
  setProgressStartTime: (time: number | null) => set({ progressStartTime: time }),
  setCurrentProgressStage: (stage: number) => set({ currentProgressStage: stage }),
  setExpectedIterationTime: (time: number) => set({ expectedIterationTime: time }),
  setSelectedDataset: (dataset: string) => {
    const trimmed = (dataset || '').trim();
    set({
      selectedDataset: trimmed,
      selectedDatasets: trimmed ? [trimmed] : [],
    });
  },
  setSelectedDatasets: (datasets: string[]) => {
    const normalized = normalizeOptimizationDatasetNames(datasets);
    const current = get().selectedDatasets;
    if (normalized.length === current.length && normalized.every((d, i) => d === current[i])) {
      return;
    }
    set({
      selectedDatasets: normalized,
      selectedDataset: normalized[0] || '',
    });
  },
  setSelectedModel: (model: string) => set({ selectedModel: model }),
  setTemperature: (temperature: number) => {
    const numTemp = Number(temperature);
    console.log('🔄 setTemperature called with:', temperature, 'converted to:', numTemp);
    set({ temperature: numTemp });
  },
  setIterations: (iterations: number) => {
    const numIterations = Number(iterations);
    const currentState = get();
    const maxIterations = currentState.iterationsMax || 10;

    // Убеждаемся что значение в допустимых пределах
    const validIterations = Math.max(1, Math.min(numIterations, maxIterations));

    set({ iterations: validIterations });
  },
  setOptimizationType: (type: 'standard' | 'with_integration') => set({ optimizationType: type }),
  setAvailableDatasets: (datasets: string[]) => set({ availableDatasets: datasets }),
  setAvailableModels: (models: string[]) => set({ availableModels: models }),
  setEmbeddingData: (data: number[]) => set({ embeddingData: data }),
  setIterationData: (data: number[]) => set({ iterationData: data }),
  setCurrentIteration: (iteration: number) => set({ currentIteration: iteration }),
  setGraphSliderValue: (value: number) => set({ graphSliderValue: value }),
  addEmbeddingPoint: (value: number) =>
    set((state: any) => ({
      embeddingData: [...state.embeddingData, value],
    })),
  addIterationPoint: (value: number) =>
    set((state: any) => ({
      iterationData: [...state.iterationData, value],
    })),
  setSelectedIterationIndex: (index: number) => set({ selectedIterationIndex: index }),

  // Действия для работы с чатами Single-Agent
  setSingleAgentChatMessages: (messages: { before: ChatMessage[]; after: ChatMessage[] }) =>
    set({ singleAgentChatMessages: messages }),
  setSingleAgentChatInputs: (inputs: { before: string; after: string }) => set({ singleAgentChatInputs: inputs }),
  setSingleAgentChatInput: (type: 'before' | 'after', value: string) =>
    set((state) => ({
      singleAgentChatInputs: {
        ...state.singleAgentChatInputs,
        [type]: value,
      },
    })),
  addSingleAgentChatMessage: (type: 'before' | 'after', message: ChatMessage) =>
    set((state) => ({
      singleAgentChatMessages: {
        ...state.singleAgentChatMessages,
        [type]: [...state.singleAgentChatMessages[type], message],
      },
    })),
  clearSingleAgentChat: () =>
    set({
      singleAgentChatMessages: { before: [], after: [] },
      singleAgentChatInputs: { before: '', after: '' },
    }),

  // Новые функции для работы с данными из бэкенда
  addBackendEmbeddingPoint: (point: { x: number; y: number }) =>
    set((state: any) => ({
      embeddingData: [...state.embeddingData, point.x, point.y],
    })),
  addBackendEvaluationPoint: (point: { x: number; y: number }) =>
    set((state: any) => ({
      iterationData: [...state.iterationData, point.x, point.y],
    })),
  clearGraphData: () =>
    set({
      embeddingData: [],
      iterationData: [],
      iterationResults: [],
      optimizedPrompts: [],
      currentIteration: 0,
      graphSliderValue: 0,
    }),

  // Функция для обработки результатов оптимизации из бэкенда
  processOptimizationResult: (result: {
    prompt?: string;
    prompts?: string[];
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    totalIterations?: number;
    temperature?: number;
  }) =>
    set((state: any) => ({
      iterationResults: [
        ...state.iterationResults,
        {
          prompt: result.prompt || '',
          embedding_point: result.embedding_point,
          evaluation_point: result.evaluation_point,
          iteration: state.currentIteration + 1,
          temperature:
            typeof result.temperature === 'number'
              ? result.temperature
              : (state.optimizedTemperature ?? state.temperature),
        },
      ],
      currentIteration: state.currentIteration + 1,
      optimizedTemperature: typeof result.temperature === 'number' ? result.temperature : state.optimizedTemperature,
    })),

  // Функция для постепенного добавления промптов
  addOptimizedPrompt: (prompt: string) =>
    set((state: any) => {
      // Если это первый промпт или массив пустой, заменяем массив
      // Иначе добавляем к существующим
      const updatedPrompts = state.optimizedPrompts.length === 0 ? [prompt] : [...state.optimizedPrompts, prompt];

      return {
        optimizedPrompts: updatedPrompts,
        isOptimized: true,
      };
    }),

  // Функция для обработки промежуточных результатов
  processIntermediateResult: (result: {
    prompt?: string;
    embedding_point?: { x: number; y: number };
    evaluation_point?: { x: number; y: number };
    quality_point?: { x: number; y: number };
    actor_tokens_point?: { x: number; y: number };
    iteration?: number;
    seq?: number; // seq from backend: 0 = baseline evaluation, 1+ = optimized iterations
    temperature?: number;
  }) =>
    set((state: any) => {
      const newPrompt = result.prompt || '';
      const incomingTemp = typeof result.temperature === 'number' ? result.temperature : undefined;
      const seq = typeof result.seq === 'number' ? result.seq : undefined;

      console.log('🔄 processIntermediateResult:', {
        newPrompt,
        seq,
        currentPrompts: state.optimizedPrompts,
        currentIteration: state.currentIteration,
        iterations: state.iterations,
        iterationResultsLength: state.iterationResults.length,
      });

      // seq: 0 - это оценочный ответ (baseline evaluation)
      // Показываем только в графике как точка 0 и в истории как step 0
      // НЕ добавляем в optimizedPrompts
      if (seq === 0) {
        console.log('📊 seq: 0 - baseline evaluation, adding to iterationResults only (not to optimizedPrompts)');

        // Добавляем точки на график для baseline evaluation (точка 0)
        let updatedEmbeddingData = [...state.embeddingData];
        let updatedIterationData = [...state.iterationData];

        if (result.embedding_point) {
          console.log('📈 Adding baseline embedding point (seq: 0) to graph:', result.embedding_point);
          // Проверяем, не добавляем ли мы дублирующую точку
          const existingPoint = state.embeddingData.find(
            (_: number, index: number) =>
              index % 2 === 0 &&
              state.embeddingData[index] === result.embedding_point!.x &&
              state.embeddingData[index + 1] === result.embedding_point!.y
          );
          if (!existingPoint) {
            // Если это первая точка, заменяем массив, иначе добавляем
            updatedEmbeddingData =
              state.embeddingData.length === 0
                ? [result.embedding_point.x, result.embedding_point.y]
                : [...state.embeddingData, result.embedding_point.x, result.embedding_point.y];
          }
        }

        if (result.evaluation_point) {
          console.log('📈 Adding baseline evaluation point (seq: 0) to graph:', result.evaluation_point);
          // Проверяем, не добавляем ли мы дублирующую точку
          const existingPoint = state.iterationData.find(
            (_: number, index: number) =>
              index % 2 === 0 &&
              state.iterationData[index] === result.evaluation_point!.x &&
              state.iterationData[index + 1] === result.evaluation_point!.y
          );
          if (!existingPoint) {
            // Если это первая точка, заменяем массив, иначе добавляем
            updatedIterationData =
              state.iterationData.length === 0
                ? [result.evaluation_point.x, result.evaluation_point.y]
                : [...state.iterationData, result.evaluation_point.x, result.evaluation_point.y];
          }
        }

        // Deduplicate baseline by seq/iteration 0
        const hasBaseline = state.iterationResults.some((r: any) => r.seq === 0 || r.iteration === 0);
        if (hasBaseline) {
          console.log('♻️ Skipping duplicate baseline (seq=0) for single-agent');
          return {
            embeddingData: updatedEmbeddingData,
            iterationData: updatedIterationData,
            currentIteration: state.currentIteration,
            optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature,
          };
        }

        return {
          iterationResults: [
            ...state.iterationResults,
            {
              prompt: newPrompt,
              embedding_point: result.embedding_point,
              evaluation_point: result.evaluation_point,
              quality_point: result.quality_point,
              actor_tokens_point: result.actor_tokens_point,
              iteration: 0, // seq: 0 соответствует iteration: 0
              seq: 0,
              temperature: incomingTemp ?? state.temperature,
            },
          ],
          embeddingData: updatedEmbeddingData,
          iterationData: updatedIterationData,
          currentIteration: state.currentIteration,
          optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature,
        };
      }

      // seq: 1, 2, 3... - это оптимизированные конфигурации
      // Добавляем в optimizedPrompts и в iterationResults
      const currentOptimizedCount = state.optimizedPrompts.length;
      const maxIterations = state.iterations || 4;

      // Проверяем лимит промптов. Если превышен — не добавляем в optimizedPrompts,
      // но ВСЕ РАВНО добавляем точку и iterationResults (важно для restore и графиков).
      const willAppendPrompt = currentOptimizedCount < maxIterations;

      // Правильно нумеруем итерацию для оптимизированных промптов
      // seq: 1 -> iteration: 1, seq: 2 -> iteration: 2, etc.
      const optimizedIterationNumber = seq !== undefined ? seq : currentOptimizedCount + 1;

      // Deduplicate by seq/iteration: skip if already have this step
      const alreadyExists = state.iterationResults.some((r: any) => {
        const existingSeqOrIter = typeof r.seq === 'number' ? r.seq : r.iteration;
        return existingSeqOrIter === optimizedIterationNumber;
      });

      const updatedPrompts =
        !alreadyExists && willAppendPrompt ? [...state.optimizedPrompts, newPrompt] : state.optimizedPrompts;

      // Добавляем точки на график для оптимизированных промптов
      let updatedEmbeddingData = [...state.embeddingData];
      let updatedIterationData = [...state.iterationData];

      if (result.embedding_point) {
        console.log(`📈 Adding optimized embedding point (seq: ${seq}) to graph:`, result.embedding_point);
        // Проверяем, не добавляем ли мы дублирующую точку
        const existingPoint = state.embeddingData.find(
          (_: number, index: number) =>
            index % 2 === 0 &&
            state.embeddingData[index] === result.embedding_point!.x &&
            state.embeddingData[index + 1] === result.embedding_point!.y
        );
        if (!existingPoint) {
          updatedEmbeddingData = [...state.embeddingData, result.embedding_point.x, result.embedding_point.y];
        }
      }

      if (result.evaluation_point) {
        console.log(`📈 Adding optimized evaluation point (seq: ${seq}) to graph:`, result.evaluation_point);
        // Проверяем, не добавляем ли мы дублирующую точку
        const existingPoint = state.iterationData.find(
          (_: number, index: number) =>
            index % 2 === 0 &&
            state.iterationData[index] === result.evaluation_point!.x &&
            state.iterationData[index + 1] === result.evaluation_point!.y
        );
        if (!existingPoint) {
          updatedIterationData = [...state.iterationData, result.evaluation_point.x, result.evaluation_point.y];
        }
      }

      console.log(
        `📝 Updated prompts (optimized): ${updatedPrompts.length}/${maxIterations} (willAppend=${willAppendPrompt})`
      );
      console.log(
        `📈 Total embedding points: ${updatedEmbeddingData.length / 2}, Total evaluation points: ${updatedIterationData.length / 2}`
      );

      // If this iteration already exists, only update graph data; don't append duplicates
      if (alreadyExists) {
        console.log(`♻️ Skipping duplicate single-agent result for seq=${optimizedIterationNumber}`);
        return {
          optimizedPrompts: updatedPrompts,
          embeddingData: updatedEmbeddingData,
          iterationData: updatedIterationData,
          currentIteration: state.currentIteration,
          isOptimized: true,
          optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature,
        };
      }

      return {
        optimizedPrompts: updatedPrompts,
        iterationResults: [
          ...state.iterationResults,
          {
            prompt: newPrompt,
            embedding_point: result.embedding_point,
            evaluation_point: result.evaluation_point,
            quality_point: result.quality_point,
            actor_tokens_point: result.actor_tokens_point,
            iteration: optimizedIterationNumber,
            seq: seq,
            temperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature,
          },
        ],
        embeddingData: updatedEmbeddingData,
        iterationData: updatedIterationData,
        currentIteration: optimizedIterationNumber,
        isOptimized: true,
        optimizedTemperature: incomingTemp ?? state.optimizedTemperature ?? state.temperature,
      };
    }),

  resetOptimization: () =>
    set({
      initialPrompt: '',
      botPrompt: '',
      selectedPrompt: '',
      selectedPromptIndex: '',
      optimizedPrompts: [],
      iterationResults: [],
      isOptimized: false,
      isOptimizing: false,
      optimizationProgress: 0,
      progressStartTime: null,
      currentProgressStage: 0,
      embeddingData: [],
      iterationData: [],
      currentIteration: 0,
      graphSliderValue: 0,
      selectedIterationIndex: 0,
    }),

  // Функция для сохранения результатов в историю
  saveToHistory: async () => {
    try {
      // const { autoTune } = await import('../../services/api'); // Временно отключено
      const state = get();

      console.log('💾 saveToHistory called with state:', {
        optimizedPromptsLength: state.optimizedPrompts.length,
        optimizedPrompts: state.optimizedPrompts,
        botPrompt: state.botPrompt,
        selectedDataset: state.selectedDataset,
        selectedModel: state.selectedModel,
        temperature: state.optimizedTemperature ?? state.temperature,
        iterations: state.iterations,
        iterationResultsLength: state.iterationResults.length,
      });

      if (state.optimizedPrompts.length > 0 && state.botPrompt) {
        // Создаем массив steps для истории
        const steps: Array<{
          step_number: number;
          prompt: string;
          embedding_point?: { x: number; y: number };
          evaluation_point?: { x: number; y: number };
          timestamp: string;
          model_name: string;
          temperature: number;
        }> = [];

        // Step 0: baseline evaluation с данными конфигурации
        const baselineResult = state.iterationResults.find((r) => r.seq === 0 || r.iteration === 0);
        const step0Prompt = baselineResult?.prompt || state.botPrompt;
        const step0Temperature = baselineResult?.temperature ?? state.temperature;
        const step0Model = state.selectedModel;

        steps.push({
          step_number: 0,
          prompt: step0Prompt,
          embedding_point: baselineResult?.embedding_point,
          evaluation_point: baselineResult?.evaluation_point,
          timestamp: new Date().toISOString(),
          model_name: step0Model,
          temperature: step0Temperature,
        });

        // Добавляем остальные steps из iterationResults (seq: 1, 2, 3...)
        state.iterationResults
          .filter((r) => r.seq !== 0 && r.seq !== undefined && r.seq > 0) // Только оптимизированные (seq: 1+)
          .sort((a, b) => (a.seq ?? a.iteration) - (b.seq ?? b.iteration)) // Сортируем по seq
          .forEach((result) => {
            const seq = result.seq ?? result.iteration;
            steps.push({
              step_number: seq,
              prompt: result.prompt,
              embedding_point: result.embedding_point,
              evaluation_point: result.evaluation_point,
              timestamp: new Date().toISOString(),
              model_name: step0Model, // Используем модель из конфигурации
              temperature: result.temperature ?? step0Temperature,
            });
          });

        const saveData = {
          run_id: `run_${Date.now()}`, // Generate a unique run ID
          initial_prompt: state.botPrompt,
          optimized_prompts: state.optimizedPrompts,
          dataset_name:
            formatOptimizationDatasetNameForApi(
              state.selectedDatasets.length > 0
                ? state.selectedDatasets
                : state.selectedDataset
                  ? [state.selectedDataset]
                  : []
            ) ?? '',
          model_name: state.selectedModel,
          temperature: state.optimizedTemperature ?? state.temperature,
          iterations: state.iterations,
          steps: steps, // Добавляем массив steps с step 0
        };

        console.log('📤 Sending to saveToHistory API:', saveData);
        console.log('📋 Steps array:', steps);

        // Временно отключаем сохранение в историю, так как эндпоинт может не существовать
        // const response = await autoTune.saveToHistory(saveData);
        // console.log('✅ Optimization results saved to history:', response);
        console.log('⚠️ saveToHistory temporarily disabled - endpoint may not exist');
      } else {
        console.warn('⚠️ Cannot save to history: no optimized prompts or bot prompt');
      }
    } catch (error) {
      console.error('❌ Failed to save optimization results to history:', error);
      // Не выбрасываем ошибку, чтобы не прерывать работу приложения
      // throw error;
    }
  },

  // Функция для загрузки конфигурации из истории
  loadFromHistory: (historyItem: {
    initial_prompt: string;
    optimized_prompts: string[];
    dataset_name: string | string[];
    model_name: string;
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt?: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name?: string;
      temperature?: number;
      data?: {
        'master-agent'?: {
          name: string;
          prompt: string;
          model_name: string;
          temperature: number;
          description?: string;
          reminder_prompt?: string;
        };
        'worker-agents'?: Array<{
          name: string;
          prompt: string;
          model_name: string;
          temperature: number;
          description?: string;
          mode?: 'synchronous' | 'asynchronous';
          reminder_prompt?: string;
        }>;
      };
    }>;
  }) => {
    console.log('🔄 loadFromHistory called with:', historyItem);

    // Создаем iterationResults с точками графиков
    let iterationResults: Array<{
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      iteration: number;
      seq?: number;
      temperature?: number;
    }> = [];

    // Добавляем все шаги оптимизации, включая первый
    if (historyItem.steps && historyItem.steps.length > 0) {
      // Проверяем, есть ли step 0 и заполнен ли он
      const step0 = historyItem.steps.find((s) => s.step_number === 0);

      // Функция для извлечения данных из шага (из data или прямых полей)
      const extractStepData = (step: any) => {
        // Если есть данные в step.data, используем их
        const stepDataMaster = step.data?.['master-agent'];
        if (stepDataMaster && (!step.prompt || !step.model_name)) {
          return {
            prompt: step.prompt || stepDataMaster.prompt || '',
            model_name: step.model_name || stepDataMaster.model_name || '',
            temperature: step.temperature !== undefined ? step.temperature : (stepDataMaster.temperature ?? 0),
          };
        }
        // Иначе используем прямые поля
        return {
          prompt: step.prompt || '',
          model_name: step.model_name || '',
          temperature: step.temperature ?? 0,
        };
      };

      // Если step 0 отсутствует или пустой, создаем его из конфигурации
      const isEmptyStep0 = !step0 || (!step0.prompt && !step0.model_name && !step0.data?.['master-agent']);

      if (isEmptyStep0) {
        // Создаем step 0 с данными конфигурации
        const step0Data: {
          prompt: string;
          embedding_point?: { x: number; y: number };
          evaluation_point?: { x: number; y: number };
          iteration: number;
          seq: number;
          temperature: number;
        } = {
          prompt: historyItem.initial_prompt,
          iteration: 0,
          seq: 0,
          temperature: historyItem.temperature,
        };

        // Если есть step 0 с данными точек, сохраняем их
        if (step0) {
          step0Data.embedding_point = step0.embedding_point;
          step0Data.evaluation_point = step0.evaluation_point;
        }

        iterationResults.push(step0Data);
      } else {
        // Step 0 существует, извлекаем данные
        const step0Extracted = extractStepData(step0);
        iterationResults.push({
          prompt: step0Extracted.prompt,
          embedding_point: step0.embedding_point,
          evaluation_point: step0.evaluation_point,
          iteration: 0,
          seq: 0,
          temperature: step0Extracted.temperature,
        });
      }

      // Добавляем остальные шаги оптимизации (step_number > 0)
      historyItem.steps
        .filter((step) => step.step_number > 0)
        .sort((a, b) => a.step_number - b.step_number)
        .forEach((step) => {
          const stepExtracted = extractStepData(step);
          iterationResults.push({
            prompt: stepExtracted.prompt,
            embedding_point: step.embedding_point,
            evaluation_point: step.evaluation_point,
            iteration: step.step_number,
            seq: step.step_number,
            temperature: stepExtracted.temperature,
          });
        });
    } else {
      // Fallback: если нет steps, создаем базовые iterationResults
      iterationResults = historyItem.optimized_prompts.map((prompt, index) => ({
        prompt: prompt,
        iteration: index + 1,
      }));
    }

    // Создаем данные для графиков из iterationResults
    const embeddingData: number[] = [];
    const iterationData: number[] = [];

    iterationResults.forEach((result) => {
      if (result.embedding_point) {
        embeddingData.push(result.embedding_point.x, result.embedding_point.y);
      }
      if (result.evaluation_point) {
        iterationData.push(result.evaluation_point.x, result.evaluation_point.y);
      }
    });

    const historyDatasets = normalizeOptimizationDatasetNames(historyItem.dataset_name);
    set({
      initialPrompt: historyItem.initial_prompt, // Устанавливаем initialPrompt для отображения в Initial Configuration
      botPrompt: historyItem.initial_prompt, // Устанавливаем botPrompt тоже
      optimizedPrompts: historyItem.optimized_prompts,
      selectedDatasets: historyDatasets,
      selectedDataset: historyDatasets[0] || '',
      selectedModel: historyItem.model_name,
      temperature: historyItem.temperature,
      iterations: historyItem.iterations, // Используем количество итераций из истории
      iterationsMax: 10, // Оставляем стандартный максимум
      isOptimized: true,
      selectedIterationIndex: 0,
      currentIteration: historyItem.iterations, // Используем количество итераций из истории
      iterationResults: iterationResults,
      embeddingData: embeddingData,
      iterationData: iterationData,
    });

    console.log('✅ loadFromHistory completed:', {
      iterations: historyItem.iterations,
      iterationResultsCount: iterationResults.length,
      embeddingDataPoints: embeddingData.length / 2,
      iterationDataPoints: iterationData.length / 2,
    });
  },
});
